import { ChangeEvent, FormEvent, useContext, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { AuthContext } from "../../contexts/AuthContext";
import useLogin from "../../services/useLogin";
import { IAuthContext } from "../../types/auth";
import styles from "./styles.module.css";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  FormGroup,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ApiError, getApiErrorMessage } from "../../utils/apiError";

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { tokens } = useContext(AuthContext) as IAuthContext;
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const { error, isError, isLoading, mutate, reset } = useLogin(
    form.email,
    form.password,
  );

  useEffect(() => {
    if (tokens.access !== null && tokens.refresh !== null) {
      let to = "/";
      const slug = searchParams.get("slug");
      const page = searchParams.get("page");

      if (slug) {
        to += slug;
      }

      if (page) {
        to += `/${page}`;
      }

      navigate(to, { replace: true });
    }
  }, [navigate, searchParams, tokens]);

  const handleChange =
    (key: "email" | "password") => (e: ChangeEvent<HTMLInputElement>) => {
      setForm((currentForm) => ({
        ...currentForm,
        [key]: e.target.value,
      }));
      if (isError) reset();
    };

  const handleCheck = (e: ChangeEvent<HTMLInputElement>) => {
    setShowPassword(e.target.checked);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutate();
  };

  const loginError = error as ApiError | null;
  const errorMessage =
    loginError?.status === 401
      ? "E-mail ou senha incorretos. Confira os dados e tente novamente."
      : getApiErrorMessage(
          error,
          "Não foi possível entrar agora. Verifique sua conexão e tente novamente.",
        );

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.innerContainer}>
          <Typography variant="h5" component="h1">
            Olá, parceira!
          </Typography>
          <Typography variant="body1" component="h1">
            Faça seu login para começar a montar sua cesta da semana!
          </Typography>
          <form className={styles.form} noValidate onSubmit={handleSubmit}>
            <Collapse in={isError}>
              <Alert severity="error" onClose={reset} sx={{ mb: 1 }}>
                {errorMessage}
              </Alert>
            </Collapse>
            <TextField
              autoComplete="email"
              autoFocus
              error={isError}
              fullWidth
              helperText={isError ? "Revise o e-mail informado." : undefined}
              label="E-mail"
              onChange={handleChange("email")}
              value={form.email}
            />
            <TextField
              autoComplete="current-password"
              error={isError}
              fullWidth
              helperText={isError ? "Revise a senha informada." : undefined}
              label="Senha"
              onChange={handleChange("password")}
              type={showPassword ? "text" : "password"}
              value={form.password}
            />
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox checked={showPassword} onChange={handleCheck} />
                }
                label="Mostrar senha"
              />
            </FormGroup>
            <Button
              disabled={isLoading || form.email === "" || form.password === ""}
              type="submit"
              variant="contained"
            >
              {isLoading ? "Entrando..." : "Entrar"}
            </Button>
          </form>
          <Box sx={{ color: theme.palette.primary.main, marginTop: "1rem" }}>
            <Link to="/forgot-password">Esqueci minha senha</Link>
          </Box>
          <Box sx={{ marginTop: "1rem" }}>
            Não tem acesso?{" "}
            <Box sx={{ color: theme.palette.primary.main, display: "inline" }}>
              <Link to="/signup">Cadastre-se!</Link>
            </Box>
          </Box>
        </div>
      </div>
    </div>
  );
};

export default Login;
